import { path, pathOr } from 'ramda';

import { Box, Button, Tooltip, Text } from '@frontend/components';

import { useAppSelector } from 'store/hooks';
import { getActiveGroup } from 'store/session/selectors';

import { useUserGroupCollectionContext } from 'utils/multisite';

const HighlightOrDisplay = ({
  allowNestedLink,
  field,
  result,
  useDataFont = false,
  nonHighlightTextColor = 'feature.primary',
  textProps = {},
  showOnlyIfHighlighted = false,
  resultOwnerId,
  ...rest
}) => {
  const activeGroup = useAppSelector(getActiveGroup);
  const ownerId = resultOwnerId ?? result.data.ownerId;
  const isFromOutsideGroup = ownerId && ownerId !== activeGroup.id;
  const { initChangeSite } = useUserGroupCollectionContext();
  const linkProps = isFromOutsideGroup
    ? {
        onClick: () =>
          initChangeSite({
            nextUrl: result.path,
            userGroupId: ownerId,
          }),
      }
    : { href: result.path };

  if (pathOr(false, ['highlight', field], result)) {
    // for arrays we need to check if each element has matched
    const fieldData = Array.isArray(path(['data', field], result))
      ? path(['data', field], result)
      : [path(['data', field], result)];
    const normalizedHighlight = path(['highlight', field], result).map(
      (highlightString) =>
        highlightString.replace(/<em>/g, '').replace(/<\/em>/g, ''),
    );
    const output = [];
    fieldData.forEach((data) => {
      const highlightIndex = normalizedHighlight.findIndex((highlight) =>
        String(data).includes(highlight),
      );
      if (highlightIndex >= 0) {
        // higlights will only include a subset of long search fields
        const prependEllipses =
          String(data).length !== normalizedHighlight[highlightIndex].length &&
          String(data).indexOf(normalizedHighlight[highlightIndex]) > 0;
        const postpendEllipses =
          String(data).length !== normalizedHighlight[highlightIndex].length &&
          String(data).indexOf(normalizedHighlight[highlightIndex]) +
            normalizedHighlight[highlightIndex].length <
            String(data).length;
        // get the highlighted entry
        const highlightedData = path(['highlight', field], result)[
          highlightIndex
        ];
        const highlightOutput = [];
        // identify highlights
        const fragmentedHighlights = highlightedData.split('<em>');
        if (prependEllipses) {
          highlightOutput.push({
            type: 'text',
            text: '...',
          });
        }
        fragmentedHighlights.forEach((fragmentedHighlightString) => {
          if (fragmentedHighlightString.includes('</em>')) {
            // we have found a highlight
            fragmentedHighlightString.split('</em>').forEach((text, index) => {
              if (index == 0) {
                highlightOutput.push({
                  type: 'highlight',
                  text: text,
                });
              } else {
                highlightOutput.push({
                  type: 'text',
                  text: text,
                });
              }
            });
          } else {
            // not a highlight
            highlightOutput.push({
              type: 'text',
              text: fragmentedHighlightString,
            });
          }
        });
        output.push({
          data: highlightOutput,
        });
        if (postpendEllipses) {
          highlightOutput.push({
            type: 'text',
            text: '...',
          });
        }
      } else {
        output.push({
          data: [
            {
              type: 'text',
              text: data,
            },
          ],
        });
      }
    });
    return (
      <Box display="inline" {...rest}>
        {output.map(({ data }, idx) => (
          <span key={idx}>
            {data.map(({ text, type }, index) =>
              type === 'highlight' ? (
                allowNestedLink && result.path ? (
                  <Tooltip
                    hasArrow="true"
                    label="Click to view"
                    placement="bottom"
                  >
                    <Button
                      as="a"
                      key={index}
                      variant="link"
                      fontSize="sm"
                      fontFamily={useDataFont ? 'data' : 'default'}
                      {...linkProps}
                      {...textProps}
                    >
                      {text}
                    </Button>
                  </Tooltip>
                ) : (
                  <Text
                    as="span"
                    display="inline"
                    color="brand.secondary"
                    fontWeight="heavy"
                    fontSize="sm"
                    key={index}
                    fontFamily={useDataFont ? 'data' : 'default'}
                    {...textProps}
                  >
                    {text}
                  </Text>
                )
              ) : (
                <Text
                  as="span"
                  fontSize="sm"
                  key={index}
                  fontFamily={useDataFont ? 'data' : 'default'}
                  color={nonHighlightTextColor}
                  {...textProps}
                >
                  {text}
                </Text>
              ),
            )}
            {idx < output.length - 1 ? `, ` : null}
          </span>
        ))}
      </Box>
    );
  } else {
    if (showOnlyIfHighlighted) {
      return null;
    }

    let resultText = pathOr('--', ['data', field], result);
    if (Array.isArray(resultText)) {
      resultText = resultText.join(', ');
    }
    return (
      <Text
        as="span"
        fontSize="sm"
        fontFamily={useDataFont ? 'data' : 'default'}
        color={nonHighlightTextColor}
        {...textProps}
        {...rest}
      >
        {resultText}
      </Text>
    );
  }
};

export default HighlightOrDisplay;
